import { ClaimSystemView } from "@redotech/redo-return-operations-common/views/default-return-views";
import type { CardCopy } from "./return-record-source";

/**
 * Card copy for the needs-action views claims and warranties share. Both
 * name their records "claims" to the merchant, so one set of words serves
 * both; the view names are the same strings on both types.
 */
export const CLAIM_CARD_COPY: Record<string, CardCopy> = {
  [ClaimSystemView.PARTIALLY_PROCESSED]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t("{{count}} claims still have items waiting to be processed", {
        count,
      }),
      body: t(
        "Some items in these claims were already processed, but one or more items were left unfinished. Finish processing the remaining items so the claims can be completed and customers receive everything they're owed.",
      ),
    }),
  },
  [ClaimSystemView.FAILED_RMA_CREATION]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t("{{count}} claims couldn't create an RMA", { count }),
      body: t(
        "We weren't able to create the RMA for these claims in your connected system. This can happen when the integration rejects the request or required information is missing. Review the errors and retry RMA creation after resolving the issue.",
      ),
    }),
  },
  [ClaimSystemView.LABEL_CREATION_FAILED]: {
    tone: "error",
    copy: (t, count) => ({
      title: t("{{count}} claims couldn't generate a shipping label", {
        count,
      }),
      body: t(
        "We couldn't create labels for these customers. This is commonly caused by an invalid address, unavailable shipping rate, or carrier configuration issue. Review each claim, fix the issue, and create a new label.",
      ),
    }),
  },
  [ClaimSystemView.REFUND_PAYMENT_FAILED]: {
    tone: "error",
    copy: (t, count) => ({
      title: t("{{count}} claim refunds failed to send", { count }),
      body: t(
        "These claims were processed, but Shopify reported that the refund payment failed. The customers may not have received their money. Reissue the refunds in Shopify, then mark the issue as resolved here.",
      ),
    }),
  },
  [ClaimSystemView.TRACKING_EXCEPTION]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t("{{count}} claim shipments have a delivery problem", { count }),
      body: t(
        "These shipments have a carrier exception, such as being returned to the sender or encountering a shipping error. Review the latest tracking information and decide what to do next for each claim.",
      ),
    }),
  },
  [ClaimSystemView.DELAYED_IN_CUSTOMS]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t("{{count}} international claims are delayed in customs", {
        count,
      }),
      body: t(
        "These shipments are being held by customs. Some may require additional customs information or tax IDs before they can continue. Review the affected shipments and provide any missing information.",
      ),
    }),
  },
};
